import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, BarChart3, Lightbulb, Award } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PricingPage = () => {
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState({
    starter: { credits: 100, price: 10.00, name: "Starter Pack" },
    professional: { credits: 500, price: 45.00, name: "Professional Pack" },
    enterprise: { credits: 1000, price: 80.00, name: "Enterprise Pack" }
  });

  const handlePurchase = async (packageId) => {
    setLoading(true);
    
    try {
      const userId = localStorage.getItem('userId') || 'demo_user';
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/payment/create-session?package_id=${packageId}&user_id=${userId}&origin_url=${encodeURIComponent(originUrl)}`);
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Payment initiation failed:', error);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Credits Package</h1>
            <p className="text-xl text-gray-600 mb-8">
              Unlock premium features and AI-powered recommendations
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-2xl mx-auto">
              <p className="text-yellow-800 text-sm">
                💡 <strong>Your 30-day trial has ended.</strong> Purchase credits to continue accessing premium features.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Object.entries(packages).map(([packageId, pkg]) => (
              <Card key={packageId} className={`p-8 relative ${packageId === 'professional' ? 'border-2 border-blue-500 scale-105' : ''}`}>
                {packageId === 'professional' && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-1">
                    Most Popular
                  </Badge>
                )}
                
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-blue-600">${pkg.price}</span>
                    <div className="text-gray-600 mt-2">
                      {pkg.credits} credits • ${(pkg.price / pkg.credits).toFixed(3)}/credit
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-8 text-left">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{pkg.credits} AI recommendations</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Unlimited ROI simulations</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Premium case studies</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">Export capabilities</span>
                    </div>
                    {packageId === 'enterprise' && (
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                        <span className="text-gray-700">Priority support</span>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    onClick={() => handlePurchase(packageId)}
                    disabled={loading}
                    size="lg"
                    className={`w-full py-3 font-semibold ${
                      packageId === 'professional' 
                        ? 'bg-blue-600 hover:bg-blue-700' 
                        : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {loading ? 'Processing...' : `Purchase ${pkg.name}`}
                  </Button>
                  
                  <p className="text-xs text-gray-500 mt-3">
                    Secure payment via Stripe • Cancel anytime
                  </p>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Smart QRM Navigator Credits?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="p-6">
                <div className="text-blue-600 mb-4">
                  <Lightbulb className="h-8 w-8 mx-auto" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">AI-Powered Insights</h3>
                <p className="text-gray-600 text-sm">
                  Get personalized manufacturing improvement recommendations powered by GPT-5
                </p>
              </Card>
              <Card className="p-6">
                <div className="text-green-600 mb-4">
                  <BarChart3 className="h-8 w-8 mx-auto" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">ROI Calculator</h3>
                <p className="text-gray-600 text-sm">
                  Simulate real financial impact with industry-benchmarked calculations
                </p>
              </Card>
              <Card className="p-6">
                <div className="text-purple-600 mb-4">
                  <Award className="h-8 w-8 mx-auto" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Proven Results</h3>
                <p className="text-gray-600 text-sm">
                  Based on real success stories with significant throughput improvements
                </p>
              </Card>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Need help choosing the right package?</p>
            <Button variant="outline" onClick={() => window.location.href = '/'}>
              ← Back to Homepage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
